import React, { useState, useEffect, useRef } from "react";
import {
  CheckIcon,
  ReferencesIcon,
  WarningCircleIcon,
} from "../../../common/Icons";
import { applicationsApi } from "../../../services/api";
import {
  ApplicationStatusDto,
  Reference,
  ShareReferencesContentProps,
} from "../../../common/types";
import { getStorage } from "../../../common/storageHelper";
import { customerInfoIdValue } from "../../../common/helperConstants";
import {
  AuthorizationAgreementText,
  CustomerInfoIdNotFoundText,
  DataSubmittedSuccessfullyText,
  SomethingWentWrongText,
} from "../../../common/messageContainer";
import { toast } from "react-toastify";
import CircleSpinner from "../../../common/loader/circleSpinner";

const ShareReferencesContent: React.FC<ShareReferencesContentProps> = ({
  onNextStep,
  mainContentClassName,
  markStepAsCompleted,
  currentStepId,
}) => {
  const initialReference: Reference = {
    name: "",
    phone: "",
    relationship: "",
  };
  const [references, setReferences] = useState<Reference[]>(
    Array(6)
      .fill(null)
      .map(() => ({ ...initialReference })),
  );
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [isLoader, setIsLoader] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [showTermsPopup, setShowTermsPopup] = useState(false);
  const [canAgree, setCanAgree] = useState(false);
  const [dealLaneReferences, setDealLaneReferences] = useState<boolean | null>(
    null,
  );
  const [phoneValidStatus, setPhoneValidStatus] = useState<(boolean | null)[]>(
    Array(6).fill(false),
  );
  const [phoneValidationErrors, setPhoneValidationErrors] = useState<
    (string | null)[]
  >(Array(6).fill(null));
  const [twilioResponse, setTwilioResponse] = useState<(string | null)[]>(
    Array(6).fill(null),
  );

  const termsContentRef = useRef<HTMLDivElement | null>(null);
  const [branchNumber, setBranchNumber] = useState<string>("");
  const isThreeReferenceBranch = ["93", "94", "99"].includes(branchNumber);

  const infoId = getStorage(customerInfoIdValue);
  // Fetch existing references on component mount
  useEffect(() => {
    fetchExistingReferences();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchExistingReferences = async () => {
    if (!infoId) {
      toast.error(CustomerInfoIdNotFoundText);
      localStorage.clear();
      sessionStorage.clear();
      window.location.reload();
      return;
    }

    try {
      setIsLoader(true);

      const appData: ApplicationStatusDto =
        await applicationsApi.getApplication(infoId);
      if (appData && appData.references && Array.isArray(appData.references)) {
        setBranchNumber(appData.branchNumber ?? "");
        setDealLaneReferences(appData.dealLaneReferences);
        const existReferences: Reference[] = appData.references;
        if (existReferences && Array.isArray(existReferences)) {
          // Create a completely new array instead of modifying the existing one
          const updatedReferences = Array(6)
            .fill(null)
            .map(() => ({ ...initialReference }));

          // Fill in the existing references data
          existReferences.forEach((ref: any, index: number) => {
            if (index < 6) {
              updatedReferences[index] = {
                name: ref.name || ref.Name || "",
                phone: ref.phone || ref.Phone || "",
                relationship: ref.relationship || ref.Relationship || "",
              };
              setPhoneValidStatus((prev) => {
                const updated = [...prev];
                updated[index] = ref.isPhoneNoValid;
                return updated;
              });
              setPhoneValidationErrors((prev) => {
                const updated = [...prev];
                updated[index] =
                  ref.isPhoneNoValid == false
                    ? "We couldn't verify this phone number. Please check that it's entered correctly, or provide a different phone number for your reference"
                    : "";
                return updated;
              });
            }
          });

          setReferences(updatedReferences);
        }
      } else {
        toast.error(SomethingWentWrongText);
      }
    } catch (error: any) {
      toast.error(SomethingWentWrongText);
    } finally {
      setIsLoader(false);
      setIsInitialLoad(false);
    }
  };

  useEffect(() => {
    if (showTermsPopup && termsContentRef.current) {
      const el = termsContentRef.current;

      const hasScroll = el.scrollHeight > el.clientHeight;

      // If no scroll is needed, enable Agree immediately
      if (!hasScroll) {
        setCanAgree(true);
      } else {
        setCanAgree(false);
      }
    }
  }, [showTermsPopup]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;

    const isAtBottom =
      Math.ceil(target.scrollTop + target.clientHeight) >= target.scrollHeight;

    if (isAtBottom) {
      setCanAgree(true);
    }
  };

  const handleAgree = () => {
    setAgreedToTerms(true);
    setShowTermsPopup(false);
  };

  const handleInteraction = () => {
    markStepAsCompleted(currentStepId);
  };

  const handleInputChange = (
    index: number,
    field: keyof Omit<Reference, "isTouched">,
    value: string,
  ) => {
    const newReferences = [...references];

    if (!newReferences[index]) return;

    newReferences[index][field] = value;
    if (field === "phone") {
      setPhoneValidStatus((prev) => {
        const updated = [...prev];
        updated[index] = null;
        return updated;
      });
      setPhoneValidationErrors((prev) => {
        const updated = [...prev];
        updated[index] = null;
        return updated;
      });
    }

    const stringKeys = Object.keys(newReferences[index]).filter(
      (k) => k !== "isTouched",
    ) as (keyof Omit<Reference, "isTouched">)[];

    const hasAnyValue = stringKeys.some(
      (k) => String(newReferences[index][k] || "").trim() !== "",
    );

    newReferences[index].isTouched = hasAnyValue;

    setReferences(newReferences);
  };

  //DRP-2528
  const handlePhoneBlur = async (index: number, phone: string) => {
    const digits = phone.replace(/\D/g, "");
    if (digits.length !== 10) {
      setPhoneValidStatus((prev) => {
        const updated = [...prev];
        updated[index] = false;
        return updated;
      });

      setPhoneValidationErrors((prev) => {
        const updated = [...prev];
        updated[index] = null;
        return updated;
      });
      return;
    }

    if (!infoId) return;

    try {
      setIsLoader(true);
      const result = await applicationsApi.validatePhone(infoId, digits);
      const isValid = result?.isPhoneNoValid === true;
      if (result) {
        setPhoneValidStatus((prev) => {
          const updated = [...prev];
          updated[index] = isValid;
          return updated;
        });

        setPhoneValidationErrors((prev) => {
          const updated = [...prev];
          updated[index] = isValid
            ? null
            : "We couldn't verify this phone number. Please check that it's entered correctly, or provide a different phone number for your reference";
          return updated;
        });
        setTwilioResponse((prev) => {
          const updated = [...prev];
          updated[index] = result.remarks || "";
          return updated;
        });
      }
    } catch (error: any) {
      const data = error?.response?.data as
        | { IsPhoneNoValid?: boolean; Remarks?: string }
        | undefined;

      setPhoneValidStatus((prev) => {
        const updated = [...prev];
        updated[index] = false;
        return updated;
      });

      setPhoneValidationErrors((prev) => {
        const updated = [...prev];
        updated[index] =
          data?.Remarks ||
          "We couldn't verify this phone number. Please check that it's entered correctly, or provide a different phone number for your reference";
        return updated;
      });
    }
    setIsLoader(false);
  };

  const handleAgreementChange = (checked: boolean) => {
    setAgreedToTerms(checked);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!infoId) {
      toast.error(CustomerInfoIdNotFoundText);
      localStorage.clear();
      sessionStorage.clear();
      window.location.reload();
      return;
    }

    if (!agreedToTerms) {
      toast.error(AuthorizationAgreementText);
      return;
    }

    try {
      setIsLoader(true);
      // const filteredReferences = references.filter((ref, index) => {
      //   if (index < 4) return true;
      //   return ref.isTouched === true;
      // });

      //DRP-2528
      const filteredReferences = references
        .slice(0, isThreeReferenceBranch ? 3 : 6)
        .map((ref, index) => ({
          name: ref.name?.trim() || "",
          phone: ref.phone?.trim() || "",
          relationship: ref.relationship?.trim() || "",
          isPhoneNoValid: phoneValidStatus[index],
          remarks: twilioResponse[index] || "",
        }))
        .filter((ref) =>
          [ref.name, ref.phone, ref.relationship].some((v) => v !== ""),
        );
      // Call the API to save references
      const response = await applicationsApi.saveReferences(
        infoId,
        filteredReferences,
      );

      if (response?.success) {
        handleInteraction();
        toast.success(DataSubmittedSuccessfullyText);
        setTimeout(() => {
          onNextStep();
        }, 500);
      } else {
        toast.error(SomethingWentWrongText);
      }
    } catch (error: any) {
      let errorMessage = SomethingWentWrongText;

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.errors) {
        // Handle validation errors
        errorMessage = JSON.stringify(error.response.data.errors);
      } else if (error.response?.data) {
        errorMessage =
          typeof error.response.data === "string"
            ? error.response.data
            : JSON.stringify(error.response.data);
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    } finally {
      setIsLoader(false);
    }
  };

  const referenceLabels = isThreeReferenceBranch
    ? ["*Reference 1", "*Reference 2", "*Reference 3"]
    : [
        "*Parents / Padres",
        "*Brother/Sister/Hermano(a)",
        "*Reference 1",
        "*Reference 2",
        "Reference 3",
        "Reference 4",
      ];

  const handleTermsPopup = () => {
    setShowTermsPopup(true);
  };

  const validPhoneCount = phoneValidStatus.filter((v) => v === true).length;
  const hasIncompleteOrInvalidPhone = references.some(
    (ref, index) =>
      ref.phone != null &&
      ref.phone.trim() !== "" &&
      phoneValidStatus[index] !== true,
  );

  return (
    <main className={mainContentClassName || "w-full"}>
      <div className={`${isLoader === true ? "loaderGrey" : "loaderDisplay"}`}>
        <CircleSpinner />
      </div>
      {isLoader && isInitialLoad && (
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-lg h-full">
          <div className="text-center text-gray-600">
            <div className="text-center text-gray-600">Loading Data...</div>
          </div>
        </div>
      )}
      {dealLaneReferences && (
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-lg h-full">
          <div className="text-center text-gray-600">
            <p className="text-xl font-semibold mb-2">
              You have already submitted your References. Please proceed to the
              next step.
            </p>
            <div className="mt-8 text-right md:block">
              <button
                type="button"
                className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2.5 px-6 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 text-base lg:text-[1.2rem] lg:leading-[2rem] disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Submit references and proceed to next step"
                disabled={isLoader}
                onClick={onNextStep}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
      {dealLaneReferences === false && (
        <div className="bg-white p-4 rounded-lg shadow-lg h-full">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">
            Please fill the references spots
          </h2>

          <div className="mb-6 flex flex-col md:flex-row items-center md:items-start">
            <div className="md:w-1/3 flex justify-center mb-4 md:mb-0">
              <ReferencesIcon className="w-20 h-20 text-blue-500" />
            </div>
            <div className="md:w-2/3 md:pl-4 space-y-2 text-gray-700 text-base">
              <div className="flex items-start">
                <CheckIcon className="w-5 h-5 lg:w-6 lg:h-6 text-blue-600 mr-2 mt-0.5 lg:mt-1 flex-shrink-0" />
                <span>
                  {isThreeReferenceBranch ? (
                    <span>Include three references.</span>
                  ) : (
                    <span>
                      Include two relatives (e.g., Parents, Brother/Sister).
                    </span>
                  )}
                </span>
              </div>
              <div className="flex items-start">
                <CheckIcon className="w-5 h-5 lg:w-6 lg:h-6 text-blue-600 mr-2 mt-0.5 lg:mt-1 flex-shrink-0" />
                <span>
                  Include an accurate address and phone for each reference.
                </span>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-x-6 gap-y-4 mb-6">
              {references
                .slice(0, isThreeReferenceBranch ? 3 : 6)
                .map((ref, index) => (
                  <div
                    key={index}
                    className="border border-gray-200 p-3 rounded-md"
                  >
                    <h3 className="text-lg font-medium text-gray-700 mb-2">
                      {referenceLabels[index]}
                    </h3>
                    <div className="space-y-2">
                      <div>
                        <label
                          htmlFor={`name-${index}`}
                          className="block text-base font-medium text-gray-600"
                        >
                          Name
                        </label>
                        <input
                          type="text"
                          id={`name-${index}`}
                          value={ref.name}
                          onChange={(e) =>
                            handleInputChange(index, "name", e.target.value)
                          }
                          className="mt-0.5 block w-full px-2.5 py-1.5 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-base"
                          required={
                            isThreeReferenceBranch
                              ? index < 3
                              : index < 4 || ref.isTouched
                          }
                          maxLength={100}
                          disabled={isLoader}
                        />
                      </div>
                      <div>
                        <label
                          htmlFor={`phone-${index}`}
                          className="block text-base font-medium text-gray-600"
                        >
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          id={`phone-${index}`}
                          value={ref.phone}
                          onChange={(e) => {
                            const digits = e.target.value
                              .replace(/\D/g, "")
                              .slice(0, 10);

                            const formatted =
                              digits.length <= 3
                                ? digits
                                : digits.length <= 6
                                  ? `(${digits.slice(0, 3)}) ${digits.slice(3)}`
                                  : `(${digits.slice(0, 3)}) ${digits.slice(
                                      3,
                                      6,
                                    )}-${digits.slice(6)}`;

                            handleInputChange(index, "phone", formatted);
                          }}
                          onBlur={() => handlePhoneBlur(index, ref.phone)}
                          inputMode="numeric"
                          pattern="\(\d{3}\) \d{3}-\d{4}"
                          placeholder="(123) 456-7890"
                          className="mt-0.5 block w-full px-2.5 py-1.5 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-base"
                          required={
                            isThreeReferenceBranch
                              ? index < 3
                              : index < 4 || ref.isTouched
                          }
                          disabled={isLoader}
                        />
                        {phoneValidationErrors[index] && (
                          <p className="mt-1 text-sm text-red-600 flex items-start gap-1.5">
                            <WarningCircleIcon />
                            <span>{phoneValidationErrors[index]}</span>
                          </p>
                        )}
                      </div>
                      <div>
                        <label
                          htmlFor={`relationship-${index}`}
                          className="block text-base font-medium text-gray-600"
                        >
                          Relationship
                        </label>
                        <input
                          type="text"
                          id={`relationship-${index}`}
                          value={ref.relationship}
                          onChange={(e) =>
                            handleInputChange(
                              index,
                              "relationship",
                              e.target.value,
                            )
                          }
                          className="mt-0.5 block w-full px-2.5 py-1.5 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-base"
                          required={
                            isThreeReferenceBranch
                              ? index < 3
                              : index < 4 || ref.isTouched
                          }
                          maxLength={80}
                          disabled={isLoader}
                        />
                      </div>
                    </div>
                  </div>
                ))}
            </div>

            <div className="mb-6">
              <div className="flex items-start">
                <input
                  id="terms-agreement"
                  name="terms-agreement"
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => handleAgreementChange(e.target.checked)}
                  className="h-4 w-4 lg:h-5 lg:w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mt-0.5 lg:mt-1"
                  required
                  disabled={isLoader}
                />
                <label className="ml-2 block text-base text-gray-700">
                  I have read and agree to the{" "}
                  <a
                    onClick={handleTermsPopup}
                    className="font-medium text-blue-600 hover:text-blue-500 cursor-pointer"
                  >
                    authorization to contact customer and references
                  </a>
                  .
                </label>
              </div>
            </div>

            <div className="mt-8 text-right md:flex justify-end items-center gap-2.5">
              {validPhoneCount < 2 && hasIncompleteOrInvalidPhone && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1.5">
                  <WarningCircleIcon />
                  Please fix invalid phone numbers.
                </p>
              )}

              {validPhoneCount < 2 && !hasIncompleteOrInvalidPhone && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1.5">
                  <WarningCircleIcon />
                  Please provide at least 2 references with valid phone numbers.
                </p>
              )}

              <button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2.5 px-6 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 text-base lg:text-[1.2rem] lg:leading-[2rem] border border-transparent disabled:bg-gray-100 disabled:text-gray-400 disabled:border-gray-300 disabled:shadow-none disabled:hover:shadow-none disabled:cursor-not-allowed"
                aria-label="Submit references and proceed to next step"
                disabled={isLoader || validPhoneCount < 2}
              >
                Next
              </button>
            </div>
          </form>

          {showTermsPopup && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-3">
              <div className="bg-white w-full max-w-3xl rounded-lg shadow-xl flex flex-col max-h-[70vh]">
                {/* Header */}
                <div className="px-4 py-3 border-b">
                  <h3 className="text-lg font-semibold text-gray-800">
                    Terms & Conditions
                  </h3>
                </div>

                {/* Scrollable Content */}
                <div
                  ref={termsContentRef}
                  className="px-4 py-3 overflow-y-auto text-sm text-gray-700 space-y-4"
                  onScroll={handleScroll}
                >
                  <p>
                    I authorize Dealer and its assignee to contact the above
                    Landlord, references, and any persons that I may provide
                    contact information for in the future (collectively,
                    "References"), in the event that I cannot be reached
                    directly, and further authorize Dealer and its assignee to
                    leave messages and speak with my References in connection
                    with the collection of the money I owe under my vehicle
                    purchase contract. I authorize and request Dealer, its
                    assignee and their affiliates to contact me and disclose
                    account information on my cellular phone, by e-mail, text
                    message, and voicemail, and by using prerecorded/artificial
                    voice messages and automatic telephone dialing systems, as
                    the law allows, even if it results in a charge to me, and
                    certify that I am the only person with access to any e-mail
                    account and cellular phone that I provide addresses and
                    telephone numbers for above or in the future, and I will
                    notify Dealer or its assignee of any changes to my telephone
                    number(s). I agree that Dealer and its assignee may monitor
                    and/or record telephone calls with me, for quality assurance
                    or any other purpose.
                  </p>
                  <br />
                  <p>
                    Yo autorizo al Concesionario y a su cesionario a comunicarse
                    con el propietario de casa anterior, las referencias, y
                    cualquier persona de la cual pueda proporcionar información
                    de contacto en el futuro (colectivamente, "Referencias"), en
                    caso de que no se puedan comunicar conmigo directamente, y
                    además autorizo al Concesionario y al cesionario a dejar
                    mensajes y hablar con mis Referencias en relación con el
                    cobro del dinero que debo bajo mi contrato de compra de
                    vehículo. Yo autorizo y solicito al Concesionario y a su
                    cesionario y sus afiliadas que se pongan en contacto conmigo
                    y revelen información de la cuenta por medio de teléfono
                    celular, e-mail, mensaje de texto, y correo de voz, y
                    mediante el uso de mensajes de voz pregrabados/voz
                    artificial y sistemas automáticos de marcación telefónica,
                    según la ley lo permita, incluso si resulta en un cargo para
                    mí, y certifico que soy la única persona que tiene acceso a
                    cualquier cuenta de correo electrónico y teléfono celular de
                    los cuales proporciono direcciones y números de teléfono
                    para lo anterior o en el futuro, y notificaré al
                    Concesionario o a su cesionario cualquier cambio de mi(s)
                    número(s) de teléfono(s). Acuerdo que el Concesionario y su
                    cesionario pueden monitorear y/o grabar llamadas telefónicas
                    conmigo para aseguramiento de calidad o cualquier otro
                    propósito.
                  </p>
                </div>

                {/* Footer */}
                <div className="px-4 py-3 border-t flex justify-end gap-3">
                  <button
                    onClick={handleAgree}
                    disabled={!canAgree}
                    className={`px-4 py-2 rounded-md text-white font-semibold
            ${
              canAgree
                ? "bg-blue-600 hover:bg-blue-700"
                : "bg-gray-400 cursor-not-allowed"
            }`}
                  >
                    Agree
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </main>
  );
};

export default ShareReferencesContent;
